import { useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { SearchIcon, ArrowUpDownIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { Referral } from "../types";

type AffiliateReferralsTableProps = {
  referrals: Referral[];
  isLoading: boolean;
};

type SortKey = "createdAt" | "totalVolume" | "totalCommissions";
type SortDirection = "asc" | "desc";

export const AffiliateReferralsTable = ({
  referrals,
  isLoading,
}: AffiliateReferralsTableProps) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const filteredReferrals = referrals.filter((ref) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      ref.email?.toLowerCase().includes(searchLower) ||
      ref.walletAddress?.toLowerCase().includes(searchLower) ||
      ref.id.toLowerCase().includes(searchLower)
    );
  });

  const sortedReferrals = [...filteredReferrals].sort((a, b) => {
    let aVal: number | string = 0;
    let bVal: number | string = 0;

    switch (sortKey) {
      case "createdAt":
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      case "totalVolume":
        aVal = a.totalVolume || 0;
        bVal = b.totalVolume || 0;
        break;
      case "totalCommissions":
        aVal = a.totalCommissions || 0;
        bVal = b.totalCommissions || 0;
        break;
    }

    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  const SortableHeader = ({
    label,
    sortKeyName,
  }: {
    label: string;
    sortKeyName: SortKey;
  }) => (
    <button
      onClick={() => handleSort(sortKeyName)}
      className={cn(
        "flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors hover:text-foreground",
        sortKey === sortKeyName ? "text-foreground" : "text-muted-foreground"
      )}
    >
      {label}
      <ArrowUpDownIcon className="size-3" />
    </button>
  );

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-muted rounded mb-4" />
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted/50 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none overflow-hidden">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border/50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold">Your Referrals</h3>
            <p className="text-xs text-muted-foreground">
              {referrals.length} total referrals
            </p>
          </div>

          <div className="relative w-full sm:w-auto">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search referrals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full sm:w-64"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-muted/30">
            <tr>
              <th className="w-[40%] px-4 md:px-6 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  User
                </span>
              </th>
              <th className="w-[20%] px-4 md:px-6 py-3 text-left">
                <SortableHeader label="Joined" sortKeyName="createdAt" />
              </th>
              <th className="w-[20%] px-4 md:px-6 py-3">
                <div className="flex justify-center">
                  <SortableHeader label="Volume" sortKeyName="totalVolume" />
                </div>
              </th>
              <th className="w-[20%] px-4 md:px-6 py-3">
                <div className="flex justify-end">
                  <SortableHeader label="Your Earnings" sortKeyName="totalCommissions" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedReferrals.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 md:px-6 py-8 text-center text-muted-foreground">
                  {search ? "No referrals match your search" : "No referrals yet"}
                </td>
              </tr>
            ) : (
              sortedReferrals.map((ref) => (
                <tr
                  key={ref.id}
                  className="border-t border-border/30 hover:bg-muted/20 transition-colors"
                >
                  <td className="px-4 md:px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-xs font-medium shrink-0">
                        {(ref.email?.[0] || ref.walletAddress?.[0] || "U").toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {ref.email 
                            ? `${ref.email.split('@')[0].slice(0, 3)}***@${ref.email.split('@')[1].slice(0, 3)}***`
                            : ref.walletAddress 
                              ? `${ref.walletAddress.slice(0, 4)}...${ref.walletAddress.slice(-4)}`
                              : `User ${ref.referredUserId.slice(0, 8)}...`}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          ID: {ref.referredUserId.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 text-sm text-muted-foreground">
                    {new Date(ref.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-4 md:px-6 py-3 text-center">
                    <span className="text-sm font-medium">
                      ${formatCurrency(ref.totalVolume || 0, 0)}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3 text-right">
                    <span className="text-sm font-medium text-emerald-500">
                      +${formatCurrency(ref.totalCommissions || 0, 2)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

